import { React, useState, useEffect, useCallback, memo } from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { useDispatch, useSelector } from "react-redux";
import EventIcon from "@mui/icons-material/Event";
import { IconButton, useMediaQuery, useTheme } from "@mui/material";
import { getCalendarData } from "api/tableView";

const baseStyles = {
  borderRadius: "50%",
  transition: "background-color 0.2s ease-in-out",
  "&:not(.Mui-selected)": {
    border: "none",
  },
};

const colorStyles = {
  selected: {
    ...baseStyles,
    backgroundColor: "#1976d2 !important",
    color: "white !important",
    "&:hover": {
      backgroundColor: "#1565c0 !important",
    },
  },
  zero: {
    ...baseStyles,
    backgroundColor: "#4caf50 !important",
    color: "white !important",
    "&:hover": {
      backgroundColor: "#43a047 !important",
    },
  },
  low: {
    ...baseStyles,
    backgroundColor: "#ff9800 !important",
    color: "white !important",
    "&:hover": {
      backgroundColor: "#f57c00 !important",
    },
  },
  high: {
    ...baseStyles,
    backgroundColor: "#f44336 !important",
    color: "white !important",
    "&:hover": {
      backgroundColor: "#d32f2f !important",
    },
  },
  default: baseStyles,
};

const ServerDay = memo(
  ({ highlightedDays = [], day, outsideCurrentMonth, selected, ...other }) => {
    const formattedDay = `${day.getFullYear()}-${String(
      day.getMonth() + 1
    ).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;

    const dayData = highlightedDays.find((d) => {
      const apiDate = new Date(d.date);
      return (
        `${apiDate.getFullYear()}-${String(apiDate.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(apiDate.getDate()).padStart(2, "0")}` === formattedDay
      );
    });

    const getStyles = () => {
      if (selected) return colorStyles.selected;
      if (!dayData) return colorStyles.default;

      const count = dayData.count;
      if (count === 0) return colorStyles.zero;
      if (count < 5) return colorStyles.low;
      return colorStyles.high;
    };

    return (
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        selected={selected}
        sx={getStyles()}
      />
    );
  }
);

const MIN_DATE = new Date(2023, 11, 26);
const MAX_DATE = new Date();

function DatePickerComp() {
  const [calendar, setCalendar] = useState(new Date());
  // const [events, setEvents] = useState([]);
  const [highlightedDays, setHighlightedDays] = useState([]);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const source_id = localStorage.getItem("analysisStoreId");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const selectedDate = useSelector((state) => state.customization.selectedDate);

  const fetchEvents = useCallback(async (targetDate) => {
    try {
      const response = await getCalendarData({
        year: targetDate.getFullYear(),
        month: targetDate.getMonth() + 1,
        source_id: source_id,
      });

      if (response?.data?.data) {
        // setEvents(response.data.data);
        setHighlightedDays(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchEvents(calendar);
  }, [fetchEvents, calendar]);

  const handleMonthChange = useCallback(
    (date) => {
      fetchEvents(date);
    },
    [fetchEvents]
  );

  const handleChange = useCallback(
    (date) => {
      setCalendar(date);
      dispatch({
        type: "selectedDate",
        payload: date,
      });
    },
    [dispatch]
  );

  const datePickerProps = {
    className: "cursor-pointer",
    sx: {
      bgcolor: "white",
      "& .MuiInputBase-root": {
        height: "40px",
      },
    },
    slots: { day: ServerDay },
    slotProps: {
      textField: {
        fullWidth: true,
        bgcolor: "white",
      },
      day: { highlightedDays },
    },
    format: "dd/MM/yyyy",
    id: "date-picker-inline",
    label: "Date Selected",
    value: selectedDate,
    onChange: handleChange,
    onMonthChange: handleMonthChange,
    minDate: MIN_DATE,
    maxDate: MAX_DATE,
    closeOnSelect: false,
    onOpen: () => setOpenDatePicker(true),
    onClose: () => setOpenDatePicker(false),
    open: openDatePicker,
  };

  return (
    <>
      <div className={`flex items-center ${isSmallScreen ? "hidden" : ""}`}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker {...datePickerProps} />
        </LocalizationProvider>
      </div>
      {isSmallScreen && (
        <IconButton
          sx={{ color: "black" }}
          onClick={() => setOpenDatePicker((prev) => !prev)}
        >
          <EventIcon />
        </IconButton>
      )}
    </>
  );
}

export default memo(DatePickerComp);
